import {
    afterNextRender,
    Component,
    ElementRef,
    HostListener,
    inject,
    NgZone,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    ViewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';

import {
    ComponentsModule,
    ModalModule,
    ModalService,
} from '@jht/brand-components';
import { TranslateModule, TranslateService } from '@jht/brand-translate';
import { PipesModule } from '@jht/brand-pipes';
import { DirectivesModule } from '@jht/brand-directives';
import { Subject, takeUntil } from 'rxjs';
import { TooltipModule } from 'ng2-tooltip-directive';

import { VideoData, VideoService } from '../shared/services/video.service';
import { ProductService } from './shared/product.service';

import { PinchZoomComponent } from '@meddv/ngx-pinch-zoom';
import { Config } from '../shared/config';

import { SubnavComponent } from './partials/subnav/subnav.component';
import { Section0Component } from './partials/section-0/section-0.component';
import { Section1Component } from './partials/section-1/section-1.component';
import { Section2Component } from './partials/section-2/section-2.component';
import { Section3Component } from './partials/section-3/section-3.component';
import { Section4Component } from './partials/section-4/section-4.component';
import { Section5Component } from './partials/section-5/section-5.component';
import { Section6Component } from './partials/section-6/section-6.component';
import { Section7Component } from './partials/section-7/section-7.component';
import { Section8Component } from './partials/section-8/section-8.component';
import { RequestQuoteComponent } from './shared/request-quote/request-quote.component';
import { ProductItem } from '../shared/models/product';

@Component({
    selector: 'vision2-product',
    imports: [
        CommonModule,
        ComponentsModule,
        DirectivesModule,
        ModalModule,
        TooltipModule,
        SubnavComponent,
        Section0Component,
        Section1Component,
        Section2Component,
        Section3Component,
        Section4Component,
        Section5Component,
        Section6Component,
        Section7Component,
        Section8Component,
        PinchZoomComponent,
        PipesModule,
        TranslateModule,
        RequestQuoteComponent,
    ],
    templateUrl: './product.component.html',
    styleUrl: './product.component.scss',
})
export class ProductComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();
    @ViewChild('productModal') productModal!: ElementRef;
    product: ProductItem | null = null;
    activeImageIndex = 0;
    videoData!: VideoData | null;
    currentModal = '';
    videoLoaded = false;
    isBrowser = false;

    categorySlug!: string;
    modalitySlug!: string;
    productSlug!: string;
    consoleSlug!: string;

    pdbId!: number;
    consoleId!: number;
    sellsheetLink!: string;
    webDisplayName!: string;

    translatedIcons!: string;
    tooltipTitle!: string;
    tooltipText!: string;
    readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly modalService = inject(ModalService);
    private readonly videoService = inject(VideoService);
    private readonly productService = inject(ProductService);
    private readonly translateService = inject(TranslateService);
    private readonly ngZone = inject(NgZone);
    private readonly config = inject(Config);
    private readonly platformId = inject(PLATFORM_ID);
    private readonly document = inject(DOCUMENT);

    @ViewChild(PinchZoomComponent) pinchZoom!: PinchZoomComponent;

    constructor() {
        afterNextRender(() => {
            this.ngZone.runOutsideAngular(() => {
                setTimeout(() => {
                    const zoomButton =
                        this.document?.querySelector('.pz-zoom-button');
                    zoomButton?.setAttribute('tabIndex', '0');
                    zoomButton?.addEventListener('keydown', (event: Event) => {
                        if (!(event instanceof KeyboardEvent)) {
                            return;
                        }
                        if (event.key === 'Enter') {
                            event.target?.dispatchEvent(
                                new MouseEvent('click'),
                            );
                        }
                    });
                }, 2000);
            });
        });
    }

    @HostListener('document:keydown.escape', ['$event'])
    onEscPressed(event: Event) {
        this.closeModal(this.currentModal);
    }
    ngOnInit(): void {
        this.isBrowser = isPlatformBrowser(this.platformId);
        this.translatedIcons = `url(${this.translateService.instant(
            'PRODUCT_PINCH-ZOOM_IN_ICON_URL',
        )}), url(${this.translateService.instant(
            'PRODUCT_PINCH-ZOOM_OUT_ICON_URL',
        )})`;

        this.activatedRoute.params
            .pipe(takeUntil(this.destroy$))
            .subscribe((params) => {
                this.categorySlug = params.category;
                this.modalitySlug = params.modality;
                this.productSlug = params.product;
                this.consoleSlug =
                    this.activatedRoute.snapshot.queryParams.console;
                const resolvedProduct = this.activatedRoute.snapshot.data[
                    'productData'
                ] as ProductItem | null | undefined;

                this.loadProduct(
                    this.modalitySlug,
                    this.productSlug,
                    this.consoleSlug,
                    resolvedProduct,
                );
            });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    async loadProduct(
        modalitySlug: string,
        productSlug: string,
        consoleSlug: string,
        preloadedProduct?: ProductItem | null,
    ): Promise<void> {
        try {
            this.product =
                preloadedProduct !== undefined
                    ? preloadedProduct
                    : await this.productService.loadFullProductData(
                          modalitySlug,
                          productSlug,
                          consoleSlug,
                      );

            if (this.product) {
                this.webDisplayName = this.productService.getWebDisplayName(
                    this.product,
                );

                if (this.product.id) {
                    this.pdbId = this.product.id;
                }

                const consoleId = this.product.activeConsole
                    ? +this.product.activeConsole?.['console-pdb-id']
                    : null;
                const language = this.config.language;
                const sellsheetUrl = [
                    language,
                    'sellsheet',
                    this.pdbId,
                    consoleId,
                ]
                    .filter(Boolean)
                    .join('/');

                this.sellsheetLink = this.product['sellsheet-image']
                    ? this.product['sellsheet-image']
                    : sellsheetUrl;

                if (
                    !consoleSlug &&
                    this.product.activeConsole?.['console-name']
                ) {
                    this.selectConsole(
                        this.product.activeConsole?.['console-name'],
                        true,
                        false,
                    );
                }
            }

            if (this.activeImageIndex !== 0) {
                this.activeImageIndex = 0;
            }
        } catch (error) {
            console.error('Error loading product:', error);
        }
        if (this.pinchZoom && this.pinchZoom?.scale > 1) {
            this.pinchZoom.toggleZoom();
        }
    }

    selectConsole(
        consoleName: string,
        replaceUrlHistory = false,
        loadProduct = true,
    ): void {
        this.consoleSlug = consoleName;
        if (loadProduct) {
            this.loadProduct(
                this.modalitySlug,
                this.productSlug,
                this.consoleSlug,
            );
        }

        this.router.navigate([], {
            queryParams: {
                ...this.activatedRoute.snapshot.queryParams,
                console: consoleName,
            },
            relativeTo: this.activatedRoute,
            replaceUrl: replaceUrlHistory,
        });
    }

    changeActiveImage(index: number) {
        this.activeImageIndex = index; // Update the current image
    }

    setTooltipText(header: string, body: string) {
        this.tooltipTitle = header;
        this.tooltipText = body;
    }

    initializeVideos(modalVideoId: string): void {
        if (!modalVideoId) return;

        const videoOptions = {
            autoplay: true,
            autopause: true,
            background: false,
            controls: true,
            loop: false,
            muted: false,
            responsive: false,
        };

        this.videoService
            .getVideoData(modalVideoId, { ...videoOptions })
            .then((data: any) => {
                if (data) {
                    this.videoData = data;
                    this.videoLoaded = true;
                }
            })
            .catch((error) => {
                console.error('Error initializing video:', error);
            });
    }

    openModal(modalId: string, mediaId?: string) {
        this.currentModal = modalId;
        if (modalId === 'productVideo') {
            this.videoLoaded = false;
            this.videoData = null;
            if (mediaId) {
                this.initializeVideos(mediaId);
            }
        }

        const targetNode = this.productModal.nativeElement;
        const config = { attributes: true, childList: true, subtree: false };
        const observer = new MutationObserver(() => {
            this.modalService.open(modalId);
            observer.disconnect();
        });
        observer.observe(targetNode, config);
    }

    closeModal(id: string) {
        if (id) {
            this.currentModal = '';

            if (id === 'productVideo') {
                this.videoLoaded = false;
                this.videoData = null;
            }

            this.modalService.close(id);
        }
    }
}
